import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, LayoutDashboard } from "lucide-react";
import { targetLabel } from "@/lib/drillthrough";

// Item 13: picker shown when a widget has several drillthrough targets (keyboard: arrows / Enter / Esc).
export function DrillTargetMenu({ menu, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector("button")?.focus();
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [onClose]);
  const onKeyNav = (e) => {
    const items = [...(ref.current?.querySelectorAll("button") || [])];
    const i = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === "ArrowUp") { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
  };
  // Fixed-position portal so the menu is never clipped by the widget's scroll container
  const vw = typeof window !== "undefined" ? window.innerWidth : 1e9, vh = typeof window !== "undefined" ? window.innerHeight : 1e9;
  return createPortal(
    <div ref={ref} role="menu" aria-label="Drill through to" data-testid="drillthrough-target-menu" onKeyDown={onKeyNav}
      className="fixed z-[1000] min-w-[200px] max-w-[280px] rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-xl p-1.5"
      style={{ left: Math.max(4, Math.min(menu.x, vw - 290)), top: Math.max(4, Math.min(menu.y, vh - 40 * (menu.targets.length + 1))) }}>
      <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] px-2 py-1">Drill through to…</div>
      {menu.targets.map((t) => {
        const av = menu.availability ? menu.availability(t) : { ok: true };
        return (
          <button key={t.id} role="menuitem" data-testid={`drillthrough-target-${t.id}`} disabled={!av.ok}
            title={av.ok ? undefined : `Unavailable — ${av.reason}`}
            onClick={() => { if (!av.ok) return; onClose(); menu.run(t); }}
            className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-[color:var(--surface-2)] focus:bg-[color:var(--surface-2)] text-[color:var(--ink-2)] flex items-center gap-2 outline-none disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            {t.mode === "url" ? <ExternalLink className="w-3.5 h-3.5 text-[color:var(--brand-ocean)] shrink-0" aria-hidden="true" /> : <LayoutDashboard className="w-3.5 h-3.5 text-[color:var(--brand-ocean)] shrink-0" aria-hidden="true" />}
            <span className="truncate">{targetLabel(t)}</span>
            {!av.ok ? <span data-testid={`drillthrough-target-${t.id}-unavailable`} className="ml-auto text-[9px] text-[color:var(--ink-3)]">{av.reason}</span>
              : t.open === "new_tab" ? <span className="ml-auto text-[9px] text-[color:var(--ink-3)]">new tab</span>
                : t.open === "modal" ? <span className="ml-auto text-[9px] text-[color:var(--ink-3)]">dialog</span> : null}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}
